// UI strings for the dictionary (Sanakirja): search, entry page, inflection tables.
// Finnish is written in full and never truncated; en must have exactly the same shape.
import { defineArea } from '../area';

export const dictionaryFi = {
  title: 'Sanakirja',
  searchLabel: 'Hae sanaa valyriaksi, suomeksi tai englanniksi',
  searchPlaceholder: '',
  scope: { all: 'Kaikki', hv: 'Valyria', fi: 'Suomi', en: 'Englanti' } as Record<'all' | 'hv' | 'fi' | 'en', string>,
  scopeLabel: 'Hakukieli',
  lastViewed: 'Viimeksi katsottu',
  pickOrSearch: 'Valitse sana vasemmalta tai hae.',
  results: (n: number) => (n === 1 ? '1 osuma' : `${n} osumaa`),
  noResults: (q: string) => `Ei osumia haulle ”${q}”.`,
  noResultsHint: 'Puuttuuko sana? Merkitse se tarkistettavaksi tai ehdota omaa sanaa.',
  proposeLink: 'Ehdota omaa sanaa',
  didYouMean: 'Tarkoititko',
  recognisedForm: 'Sovellus tunnisti tämän muodon; wikissä sanaa ei ole linkitetty.',
  moreSenses: (n: number) => (n === 1 ? '+ 1 muu merkitys' : `+ ${n} muuta merkitystä`),
  via: {
    form: (form: string, label: string) => `muoto ${form} (${label})`,
    formFolded: (form: string, label: string) => `lähin muoto ${form} (${label})`,
    inflected: (word: string, base: string) => `${word} → ${base}`,
    gloss: 'merkitys',
    prefix: 'alkaa samoin',
  },
  browse: 'Selaa',
  browseNew: 'Uudet ja muuttuneet',
  browseOurs: 'Omat sanat',
  browseAudio: 'Petersonin äänitteet',
  browseRecent: 'Viimeksi katsotut',
  notFound: 'Sanaa ei löydy sanakirjasta. Se on ehkä poistettu, tai sanakirja ei ole vielä päivittynyt tälle laitteelle.',
  badge: { new: 'uusi', changed: 'muuttunut', corrected: 'korjattu' } as Record<'new' | 'changed' | 'corrected', string>,
  badgeHint: {
    new: 'Sana tuli Petersonin wikistä viimeisimmässä päivityksessä.',
    changed: 'Sana on muuttunut Petersonin wikissä edellisen päivityksen jälkeen.',
    corrected: 'Korjasitte tämän virallisen tiedon yhteisellä päätöksellä tarkistussessiossa.',
  },
  meanings: 'Merkitykset',
  sense: (n: number) => `${n}.`,
  official: 'virallinen',
  noFi: 'Suomenkielistä merkitystä ei ole vielä.',
  addFi: 'Lisää suomenkielinen merkitys',
  approve: 'Hyväksy',
  editFi: 'Muokkaa',
  editTitle: 'Suomenkielinen merkitys',
  editLabel: (n: number) => `Merkitys ${n} suomeksi`,
  editEnRef: 'Englanniksi (virallinen)',
  saveApprove: 'Tallenna ja hyväksy',
  saveDraft: 'Tallenna luonnoksena',
  saveDraftHint: 'Luonnos odottaa kumppanisi hyväksyntää.',
  approveHint: 'Hyväksyt suomennoksen omalla nimelläsi.',
  queued: 'Tallennettu laitteelle · lähtee, kun yhteys palaa.',
  label: 'Tunniste',
  notes: 'Huomautukset',
  // the wiki's notes on the entry page (fix batch D2): synonyms and antonyms as HV words, the English prose labelled
  synonyms: 'Synonyymit',
  antonyms: 'Vastakohdat',
  /** "(merkitys 2)": the sense a synonym list belongs to. */
  ofSense: (n: number) => `merkitys ${n}`,
  /** After a label whose text is the wiki's English: "Huomautukset (englanniksi)". */
  inEnglish: 'englanniksi',
  etymology: 'Alkuperä',
  categories: 'Luokat',
  pronunciation: 'Ääntäminen',
  practice: 'Harjoittele ääntämistä',
  noAudio: 'Tästä sanasta ei ole Petersonin äänitettä.',
  examples: 'Esimerkit',
  allExamples: (n: number) => `Näytä kaikki ${n} esimerkkiä`,
  hearIt: 'Kuultavissa sarjassa',
  shows: 'Esiintyy sarjassa',
  noExamples: 'Wikissä ei ole esimerkkejä.',
  derived: 'Johdokset',
  related: 'Liittyvät sanat',
  wiki: 'Wikin sivu',
  duolingo: 'Duolingossa',
  inflection: 'Taivutus',
  noTable: 'Tälle sanalle ei ole taivutustaulukkoa.',
  tableUnsure: 'Vartalon ja päätteen rajaa ei ole varmistettu, joten muodot näytetään kokonaisina ilman saumaa.',
  showFull: 'Näytä kokonaiset muodot',
  showEndings: 'Näytä vain päätteet',
  tapHint: 'Napauta päätettä nähdäksesi koko muodon.',
  caseCol: 'Sija',
  personCol: 'Persoona',
  numbersShort: { sg: 'yks.', pl: 'mon.', pc: 'muut.', col: 'koll.', pcpl: 'muut. mon.', colpl: 'koll. mon.' } as Record<string, string>,
  merged: (a: string) => a,
  sharedForms: (form: string, cases: string, number: string) => `${number}: ${cases} ovat samanmuotoiset (${form}).`,
  degree: 'Vertailuaste',
  position: 'Asema',
  gender: 'Suku',
  mood: 'Tapaluokka',
  voice: 'Pääluokka',
  polarity: 'Myönteinen ja kielteinen',
  adverbial: 'Adverbimuoto',
  nonFinite: 'Nominaalimuodot',
  otherForms: 'Muut muodot',
  flagForm: 'Merkitse muoto tarkistettavaksi',
  flagFormTitle: (form: string) => `Muoto ${form}`,
  flagTable: 'Merkitse taivutus tai sauma tarkistettavaksi',
  approxNote: 'Suomen vastineet ovat likimääräisiä, eivät käännöksiä.',
  indexWait: 'Taivutetut muodot löytyvät, kun hakemisto on valmis.',
  recent: 'Viimeksi katsotut',
  entryOf: (hv: string) => `Sana ${hv}`,
  backToSearch: 'Takaisin hakuun',
  openFull: 'Avaa koko sana sanakirjassa',
  // --- A–Z index (Aakkoset): dictionary/AzIndex.tsx ---
  az: {
    title: 'Aakkoset',
    titleIn: { hv: 'Aakkoset', fi: 'Aakkoset · suomi', en: 'Aakkoset · englanti' } as Record<'hv' | 'fi' | 'en', string>,
    words: (n: number) => (n === 1 ? '1 sana' : `${n.toLocaleString('fi-FI')} sanaa`),
    shown: (n: number, total: number) => `${n.toLocaleString('fi-FI')} / ${total.toLocaleString('fi-FI')} sanaa`,
    tile: (letter: string, n: number) => (n === 0 ? `${letter}: ei sanoja` : n === 1 ? `${letter}: 1 sana` : `${letter}: ${n} sanaa`),
    back: 'Aakkoset',
    letters: 'Kirjaimet',
    groups: 'Kaksi ensimmäistä kirjainta',
    pos: 'Sanaluokka',
    posAll: 'Kaikki sanaluokat',
    show: 'Näytä',
    only: { all: 'Kaikki', learned: 'Opitut' } as Record<'all' | 'learned', string>,
    none: 'Ei sanoja näillä valinnoilla.',
    next: 'Seuraava:',
    backToList: 'Takaisin luetteloon',
  },
};

export const dictionaryEn: typeof dictionaryFi = {
  title: 'Dictionary',
  searchLabel: 'Search in High Valyrian, Finnish or English',
  searchPlaceholder: '',
  scope: { all: 'All', hv: 'Valyrian', fi: 'Finnish', en: 'English' },
  scopeLabel: 'Search in',
  lastViewed: 'Last viewed',
  pickOrSearch: 'Pick a word on the left, or search.',
  results: (n) => (n === 1 ? '1 result' : `${n} results`),
  noResults: (q) => `No results for “${q}”.`,
  noResultsHint: 'Is a word missing? Flag it for review or propose an own word.',
  proposeLink: 'Propose an own word',
  didYouMean: 'Did you mean',
  recognisedForm: 'Recognised by the app; the wiki does not link this word.',
  moreSenses: (n) => (n === 1 ? '+ 1 more meaning' : `+ ${n} more meanings`),
  via: {
    form: (form, label) => `${form} is the ${label}`,
    formFolded: (form, label) => `closest form ${form} (${label})`,
    inflected: (word, base) => `${word} → ${base}`,
    gloss: 'meaning',
    prefix: 'starts the same',
  },
  browse: 'Browse',
  browseNew: 'New and changed',
  browseOurs: 'Our own words',
  browseAudio: 'Peterson’s recordings',
  browseRecent: 'Recently viewed',
  notFound: 'This word is not in the dictionary. It may have been removed, or the dictionary on this device is not up to date yet.',
  badge: { new: 'new', changed: 'changed', corrected: 'corrected' },
  badgeHint: {
    new: 'A new word from Peterson’s wiki in the latest update.',
    changed: 'The word changed on Peterson’s wiki since the previous update.',
    corrected: 'Official data corrected by your joint decision in a review session.',
  },
  meanings: 'Meanings',
  sense: (n) => `${n}.`,
  official: 'official',
  noFi: 'No Finnish meaning yet.',
  addFi: 'Add a Finnish meaning',
  approve: 'Approve',
  editFi: 'Edit',
  editTitle: 'Finnish meaning',
  editLabel: (n) => `Meaning ${n} in Finnish`,
  editEnRef: 'In English (official)',
  saveApprove: 'Save and approve',
  saveDraft: 'Save as a draft',
  saveDraftHint: 'A draft waits for your partner’s approval.',
  approveHint: 'You approve the Finnish meaning under your name.',
  queued: 'Saved on this device · sends when you are back online.',
  label: 'Label',
  notes: 'Notes',
  synonyms: 'Synonyms',
  antonyms: 'Antonyms',
  ofSense: (n: number) => `sense ${n}`,
  inEnglish: 'in English',
  etymology: 'Origin',
  categories: 'Categories',
  pronunciation: 'Pronunciation',
  practice: 'Practise saying it',
  noAudio: 'There is no recording by Peterson for this word.',
  examples: 'Examples',
  allExamples: (n) => `Show all ${n} examples`,
  hearIt: 'Hear it in the show',
  shows: 'Appears in',
  noExamples: 'No examples on the wiki.',
  derived: 'Derived terms',
  related: 'Related to',
  wiki: 'Wiki page',
  duolingo: 'On Duolingo',
  inflection: 'Inflection',
  noTable: 'There is no inflection table for this word.',
  tableUnsure: 'The split into stem and ending is not confirmed for this word, so forms are shown whole, without a seam.',
  showFull: 'Show full forms',
  showEndings: 'Show endings only',
  tapHint: 'Tap an ending to see the whole form.',
  caseCol: 'Case',
  personCol: 'Person',
  numbersShort: { sg: 'sg.', pl: 'pl.', pc: 'pauc.', col: 'coll.', pcpl: 'pauc. pl.', colpl: 'coll. pl.' },
  merged: (a) => a,
  sharedForms: (form, cases, number) => `${number}: the ${cases} share one form, ${form}.`,
  degree: 'Degree',
  position: 'Position',
  gender: 'Gender',
  mood: 'Mood',
  voice: 'Voice',
  polarity: 'Polarity',
  adverbial: 'Adverbial form',
  nonFinite: 'Non-finite forms',
  otherForms: 'Other forms',
  flagForm: 'Flag form',
  flagFormTitle: (form) => `Form ${form}`,
  flagTable: 'Flag the inflection or the seam for review',
  approxNote: 'The Finnish equivalents are approximations, not translations.',
  indexWait: 'Inflected forms are found once the index is ready.',
  recent: 'Recently viewed',
  entryOf: (hv) => `The word ${hv}`,
  backToSearch: 'Back to search',
  openFull: 'Open the whole entry in the dictionary',
  // --- A–Z index (Aakkoset): dictionary/AzIndex.tsx ---
  az: {
    title: 'Alphabet',
    titleIn: { hv: 'Alphabet', fi: 'Alphabet · Finnish', en: 'Alphabet · English' },
    words: (n) => (n === 1 ? '1 word' : `${n.toLocaleString('en-GB')} words`),
    shown: (n, total) => `${n.toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')} words`,
    tile: (letter, n) => (n === 0 ? `${letter}: no words` : n === 1 ? `${letter}: 1 word` : `${letter}: ${n} words`),
    back: 'Alphabet',
    letters: 'Letters',
    groups: 'First two letters',
    pos: 'Part of speech',
    posAll: 'All parts of speech',
    show: 'Show',
    only: { all: 'All', learned: 'Learned' },
    none: 'No words with these choices.',
    next: 'Next:',
    backToList: 'Back to the list',
  },
};

// Messages.dict (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('dict', dictionaryFi, dictionaryEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof dictionaryFi> {}
}
